import { ColumnMappingService } from './columnMappingService'
import { StagingWorkflowState } from '@/hooks/useUploadWorkflowState'

/**
 * WorkflowValidator
 *
 * Validates workflow state to determine if user can proceed to next step.
 * Extracted from useUploadWorkflowController to separate validation logic from orchestration.
 */
export class WorkflowValidator {
  /**
   * Check if user can proceed from configuration step
   */
  static canProceedFromConfiguration(state: StagingWorkflowState): boolean {
    return state.selectedPlatform !== '' && state.selectedPeriod !== ''
  }

  /**
   * Check if user can proceed from field mapping step
   */
  static canProceedFromFieldMapping(state: StagingWorkflowState): boolean {
    const platformConfig = state.uploadStaging
      ? ColumnMappingService.getConfigForPlatform(state.uploadStaging.platform)
      : null

    const requiredIdentifierFields = platformConfig?.requiredFields.identifierFields || ['driverName']
    const requiredEarningsFields = platformConfig?.requiredFields.earningsFields || ['grossEarnings', 'netEarnings']

    const identifiersMapped = requiredIdentifierFields.every(field => {
      const isMapped = state.identifierMapping[field]
      const isDriverNameWithFirstLast = field === 'driverName' &&
        state.identifierMapping.firstName &&
        state.identifierMapping.lastName
      return isMapped || isDriverNameWithFirstLast
    })

    const earningsMapped = requiredEarningsFields.every(field => state.fieldMapping[field])

    return identifiersMapped && earningsMapped
  }

  /**
   * Check if user can proceed from driver review step
   */
  static canProceedFromDriverReview(state: StagingWorkflowState): boolean {
    // Check unmatched drivers (no matchedDriverId)
    const noMatches = state.driverMatches.filter(match => match.matchedDriverId === null)
    const unhandledNoMatches = noMatches.filter(match =>
      !state.ignoredRows.has(match.rowIndex) &&
      !state.unmatchedActions.has(match.rowIndex)
    )

    if (unhandledNoMatches.length > 0) {
      return false
    }

    // Validate that "select_existing" actions have a driver selected
    const incompleteActions = Array.from(state.unmatchedActions.values()).filter(
      action => action.action === 'select_existing' && !action.selectedDriverId
    )

    if (incompleteActions.length > 0) {
      return false
    }

    // Validate that "select_existing" overrides have a driver selected
    // and "create_new" overrides have a driver name
    const incompleteOverrides = Array.from(state.perfectMatchOverrides.values()).filter(override => {
      if (override.action === 'create_new') {
        return !override.newDriverName
      }
      if (override.action === 'select_existing') {
        return !override.selectedDriverId
      }
      return false
    })

    return incompleteOverrides.length === 0
  }

  /**
   * Check if user can proceed from driver confirmation step
   */
  static canProceedFromDriverConfirmation(_state: StagingWorkflowState): boolean {
    return true
  }

  /**
   * Check if user can proceed from earnings creation step
   */
  static canProceedFromEarningsCreation(state: StagingWorkflowState): boolean {
    return state.processingResult !== null
  }

  /**
   * Main validation method - determines if user can proceed to next step
   */
  static canProceedToNextStep(state: StagingWorkflowState): boolean {
    switch (state.step) {
      case 'configuration':
        return this.canProceedFromConfiguration(state)

      case 'field-mapping':
        return this.canProceedFromFieldMapping(state)

      case 'driver-review':
        return this.canProceedFromDriverReview(state)

      case 'driver-confirmation':
        return this.canProceedFromDriverConfirmation(state)

      case 'earnings-creation':
        return this.canProceedFromEarningsCreation(state)

      default:
        return false
    }
  }
}
